import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { CreatePayLinkDialog, PayLinksDialog } from "@/components/PaymentLinks";
import { PageHeader, Panel, EmptyState, Field, StatusBadge, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Receipt, Plus, CheckCircle, FileArrowDown, CurrencyDollar, Warning, Stack, Eye, Trash, PencilSimple, X, Bank, LinkSimple } from "@phosphor-icons/react";
import { PAYMENT_METHODS, INVOICE_PRODUCTS, FACTORING_PRODUCT } from "@/lib/constants";
import { FactoringFields } from "@/components/FactoringFields";
import { DatePicker } from "@/components/DatePicker";
import { PickTable, LineItems, loadItem, rentalItem, dispatchCharge, INVOICEABLE } from "@/components/InvoiceSources";
import { toast } from "sonner";

const num = (v) => Number(v) || 0;
const today = () => new Date().toISOString().slice(0, 10);
const plus30 = () => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

function Stat({ icon: Icon, label, value, tone }) {
  const tones = { blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", red: "bg-red-50 text-red-600", slate: "bg-slate-100 text-slate-600" };
  return <Panel className="p-4 flex items-center gap-3"><div className={`w-11 h-11 rounded-xl grid place-items-center ${tones[tone]}`}><Icon size={22} weight="duotone" /></div><div><p className="text-2xl font-heading font-extrabold text-slate-900 tabular leading-none">{value}</p><p className="text-xs text-slate-500 mt-1">{label}</p></div></Panel>;
}

export default function Invoices() {
  const { data: invoices = [], isLoading } = useList("invoices", "/invoices");
  const { data: summary } = useList("accounting-summary", "/accounting/summary");
  const { data: carriers = [] } = useList("carriers", "/carriers");
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const invalidate = useInvalidate();

  const [open, setOpen] = useState(false);
  const [carrierId, setCarrierId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(plus30());
  const [product, setProduct] = useState("Dispatch");
  const [items, setItems] = useState([]);
  const [factoring, setFactoring] = useState({ company: "", notes: "", items: [{ description: "", quantity: 1, rate: "" }] });
  const isFactoring = product === FACTORING_PRODUCT;
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [ef, setEf] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditInv = (inv) => {
    setEditInv(inv);
    setEf({
      customer: inv.customer || "", product: inv.product || "Dispatch",
      issue_date: inv.issue_date || "", due_date: inv.due_date || "",
      payment_method: inv.payment_method || "", status: inv.status || "unpaid",
      notes: inv.notes || "", tax_rate: inv.tax_rate || 0,
      items: (inv.items || []).map((it) => ({ description: it.description || "", detail: it.detail || "", quantity: it.quantity || 1, rate: it.rate || 0 })),
    });
  };
  const efItemChange = (idx, key, val) => setEf((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  const efAddItem = () => setEf((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, rate: 0 }] }));
  const efRemoveItem = (idx) => setEf((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const efTotal = () => (ef?.items || []).reduce((s, it) => s + num(it.quantity) * num(it.rate), 0);

  const saveEditInv = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/invoices/${editInv.id}`, {
        customer: ef.customer, product: ef.product, issue_date: ef.issue_date, due_date: ef.due_date,
        payment_method: ef.payment_method, status: ef.status, notes: ef.notes, tax_rate: num(ef.tax_rate),
        items: ef.items.map((it) => ({ description: it.description, detail: it.detail || "", quantity: num(it.quantity), rate: num(it.rate) })),
      });
      toast.success("Invoice updated");
      setEditInv(null); invalidate(["invoices", "accounting-summary", "ar", "ap", "carriers"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const carrier = carriers.find((c) => c.id === carrierId);
  const carrierDrivers = drivers.filter((d) => d.carrier_id === carrierId);
  const driver = carrierDrivers.find((d) => d.id === driverId);
  const { data: carrierLoads = [] } = useList(["loads", "carrier", carrierId], `/loads?carrier_id=${carrierId}`, { enabled: !!carrierId && !isFactoring });
  const { data: rentals = [] } = useList("trailer-rentals", "/trailer-rentals", { enabled: open && product === "Trailer Renting" });
  const pickLoads = carrierLoads.filter((l) => INVOICEABLE.includes((l.status || "").toLowerCase()) && (!driverId || l.driver_id === driverId));
  const hiddenCount = carrierLoads.filter((l) => !driverId || l.driver_id === driverId).length - pickLoads.length;
  const matchRentals = rentals.filter((r) => (r.carrier_name || "").trim().toLowerCase() === (carrier?.carrier_name || "").trim().toLowerCase());
  const pickRentals = matchRentals.length ? matchRentals : rentals;
  const hasRef = (ref) => items.some((it) => it.ref === ref);
  const toggleRef = (ref, make) => setItems((xs) => (xs.some((it) => it.ref === ref) ? xs.filter((it) => it.ref !== ref) : [...xs, make()]));
  const changeProduct = (p) => { setProduct(p); setItems([]); };

  const resetForm = () => { setCarrierId(""); setDriverId(""); setIssueDate(today()); setDueDate(plus30()); setItems([]); setProduct("Dispatch"); setFactoring({ company: "", notes: "", items: [{ description: "", quantity: 1, rate: "" }] }); };
  const openNew = () => { resetForm(); setOpen(true); };

  const createFactoring = async () => {
    const fItems = factoring.items.filter((it) => it.description.trim() && num(it.rate) > 0).map((it) => ({ description: it.description.trim(), quantity: num(it.quantity) || 1, rate: num(it.rate) }));
    if (!factoring.company.trim()) { toast.error("Enter the factoring company name"); return; }
    if (fItems.length === 0) { toast.error("Add at least one line item with an amount"); return; }
    setSaving(true);
    try {
      await api.post("/invoices", { customer: factoring.company.trim(), product, issue_date: issueDate, due_date: dueDate, items: fItems, notes: factoring.notes });
      toast.success("Factoring invoice created");
      setOpen(false); invalidate(["invoices", "accounting-summary", "ar", "ar-by-product"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const lineItems = items.filter((it) => it.description.trim()).map((it) => ({ description: it.description.trim(), detail: (it.detail || "").trim(), quantity: num(it.quantity) || 1, rate: num(it.rate) }));
  const lineTotal = lineItems.reduce((s, it) => s + it.quantity * it.rate, 0);
  const create = async () => {
    if (isFactoring) { await createFactoring(); return; }
    if (!carrierId) { toast.error("Select a carrier"); return; }
    if (lineItems.length === 0) { toast.error("Add at least one line item"); return; }
    if (lineTotal <= 0) { toast.error("Enter an amount for the line items"); return; }
    setSaving(true);
    try {
      const loadIds = items.filter((it) => it.ref.startsWith("load:")).map((it) => it.ref.slice(5));
      const chosenLoads = carrierLoads.filter((l) => loadIds.includes(l.id));
      await api.post("/invoices", {
        customer: carrier?.carrier_name || "", carrier_id: carrierId, driver_id: driverId || null, product,
        driver_name: driver?.name || "", issue_date: issueDate, due_date: dueDate, items: lineItems,
        load_reference: chosenLoads.map((l) => l.po_number || l.reference).filter(Boolean).join(", "), load_ids: loadIds,
      });
      toast.success("Invoice created");
      setOpen(false); invalidate(["invoices", "accounting-summary", "ar", "ar-by-product"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const { data: banks = [] } = useList("pay-carrier-banks", "/payments/carrier-banks");
  const [payLink, setPayLink] = useState({ open: false, invoice: null });
  const [linksOpen, setLinksOpen] = useState(false);
  const bankCarriers = new Set(banks.filter((b) => b.status === "active").map((b) => b.carrier_id));
  const chargeBank = async (inv) => {
    if (!window.confirm(`Debit ${money(inv.total)} for ${inv.number} from the carrier's linked bank account?`)) return;
    try {
      await api.post(`/payments/charge/${inv.id}`);
      toast.success("Bank debit started");
      invalidate(["invoices"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const markPaid = async (id) => { await api.put(`/invoices/${id}`, { status: "paid" }); toast.success("Marked paid"); invalidate(["invoices", "accounting-summary", "ar", "carriers"]); };

  const removeInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      invalidate(["invoices", "accounting-summary", "ar", "ap", "carriers"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const viewPdf = async (inv) => {
    setLoadingPdf(true);
    try {
      const { data } = await api.get(`/invoices/${inv.id}/pdf?inline=1`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setPreview({ url, number: inv.number });
    } catch (e) { toast.error("Could not load PDF"); }
    finally { setLoadingPdf(false); }
  };
  const downloadPdf = async (inv) => {
    try {
      const { data } = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Could not download PDF"); }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };
  const inp = "bg-white border-slate-300";

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Create, track and collect on your dispatch invoices"
        actions={<div className="flex gap-2"><Button variant="outline" onClick={() => setLinksOpen(true)} className="border-slate-300 bg-white text-slate-700 font-semibold" data-testid="payment-links-btn"><LinkSimple size={18} className="mr-1.5" />Payment Links</Button><Button data-testid="create-invoice-btn" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />Create Invoice</Button></div>} />
      <CreatePayLinkDialog open={payLink.open} invoice={payLink.invoice} onOpenChange={(o) => setPayLink((x) => ({ ...x, open: o }))} />
      <PayLinksDialog open={linksOpen} onOpenChange={setLinksOpen} onNew={() => { setLinksOpen(false); setPayLink({ open: true, invoice: null }); }} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat icon={CurrencyDollar} label="Outstanding" value={money(summary?.receivable)} tone="blue" />
        <Stat icon={CheckCircle} label="Collected" value={money(summary?.collected)} tone="emerald" />
        <Stat icon={Warning} label="Overdue" value={summary?.overdue_count ?? "—"} tone="red" />
        <Stat icon={Stack} label="Total Invoices" value={summary?.invoice_count ?? "—"} tone="slate" />
      </div>

      <Panel className="p-0">
        {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : invoices.length === 0 ? (
          <EmptyState icon={Receipt} title="No invoices yet" hint="Create your first dispatch invoice." action={<Button onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700"><Plus size={16} className="mr-1" />Create Invoice</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead>Invoice</TableHead><TableHead>Carrier</TableHead><TableHead>Driver</TableHead><TableHead>Issue</TableHead><TableHead>Due</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((i) => (
                  <TableRow key={i.id} className="border-slate-100" data-testid={`invoice-row-${i.id}`}>
                    <TableCell className="font-mono font-semibold text-blue-600">{i.number}</TableCell>
                    <TableCell className="font-medium text-slate-900">{i.customer}</TableCell>
                    <TableCell className="text-sm text-slate-600">{i.driver_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{i.issue_date || "—"}</TableCell>
                    <TableCell className={`text-sm ${i.status !== "paid" && i.due_date && i.due_date < today() ? "blink-due font-semibold" : "text-slate-600"}`} data-testid={`inv-due-date-${i.id}`}>{i.due_date || "—"}</TableCell>
                    <TableCell className={`font-mono font-semibold ${i.status !== "paid" ? "blink-due" : "text-slate-900"}`} data-testid={`inv-amount-${i.id}`}>{money(i.total)}</TableCell>
                    <TableCell><StatusBadge status={i.status} />{i.payment_status && <p className="text-[11px] text-slate-500 mt-1 capitalize" title={i.payment_error || ""} data-testid={`inv-paystatus-${i.id}`}>Payment: {i.payment_status}</p>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 items-center">
                        <Button size="sm" variant="outline" onClick={() => viewPdf(i)} disabled={loadingPdf} className="border-slate-300 bg-white text-slate-700 h-8" title="View PDF" data-testid={`invoice-view-${i.id}`}><Eye size={15} className="mr-1" />View</Button>
                        <button onClick={() => downloadPdf(i)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Download PDF" data-testid={`invoice-pdf-${i.id}`}><FileArrowDown size={16} /></button>
                        {i.status !== "paid" && <button onClick={() => setPayLink({ open: true, invoice: i })} className="p-1.5 text-slate-400 hover:text-blue-600" title="Create payment link" data-testid={`invoice-paylink-${i.id}`}><LinkSimple size={16} /></button>}
                        <button onClick={() => openEditInv(i)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Edit invoice" data-testid={`invoice-edit-${i.id}`}><PencilSimple size={16} /></button>
                        {i.status !== "paid" && bankCarriers.has(i.carrier_id) && !["processing", "pending"].includes(i.payment_status) && <Button size="sm" variant="outline" onClick={() => chargeBank(i)} className="border-emerald-300 bg-white text-emerald-700 h-8" title="Charge the carrier's saved card via Razorpay" data-testid={`invoice-charge-bank-${i.id}`}><Bank size={15} className="mr-1" />Charge Card</Button>}
                        {i.status !== "paid" && !["processing", "pending"].includes(i.payment_status) && <Button size="sm" onClick={() => markPaid(i.id)} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid={`invoice-paid-${i.id}`}>Mark Paid</Button>}
                        {i.status !== "paid" && !["processing", "pending", "settled"].includes(i.payment_status) && <button onClick={() => removeInvoice(i)} className="p-1.5 text-slate-400 hover:text-red-500" title="Delete invoice" data-testid={`invoice-delete-${i.id}`}><Trash size={16} /></button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      {/* CREATE INVOICE */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Create Invoice</DialogTitle><DialogDescription className="sr-only">Build a dispatch invoice from driver loads</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2">
            {!isFactoring && <Field label="Carrier *">
              <Select value={carrierId} onValueChange={(v) => { setCarrierId(v); setDriverId(""); setItems([]); }}>
                <SelectTrigger className={inp} data-testid="inv-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>{carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.carrier_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>}
            <Field label="Product">
              <Select value={product} onValueChange={changeProduct}>
                <SelectTrigger className={inp} data-testid="inv-product"><SelectValue /></SelectTrigger>
                <SelectContent>{INVOICE_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Issue Date"><DatePicker value={issueDate} onChange={setIssueDate} className={inp} testId="inv-issue" /></Field>
            <Field label="Due Date"><DatePicker value={dueDate} onChange={setDueDate} className={inp} testId="inv-due" /></Field>
            {!isFactoring && <div className="sm:col-span-2">
              <Field label="Driver filter (optional)">
                <Select value={driverId || "all"} onValueChange={(v) => setDriverId(v === "all" ? "" : v)} disabled={!carrierId}>
                  <SelectTrigger className={inp} data-testid="inv-driver"><SelectValue placeholder={carrierId ? "All drivers" : "Pick carrier first"} /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All drivers</SelectItem>{carrierDrivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>}
          </div>

          {isFactoring && <FactoringFields value={factoring} onChange={setFactoring} />}

          {!isFactoring && carrier && (
            <div className="space-y-5 mt-1">
              <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="inv-billto-preview">
                <span><b className="text-slate-900">{carrier.carrier_name}</b></span><span>EIN {carrier.ein || <i className="text-amber-600">not set (add it in Active Carriers)</i>}</span><span>{carrier.address || "No address"}</span><span>Dispatch rate {carrier.dispatch_charges_rate || "—"}</span>
              </div>
              {product === "Trailer Renting" && (
                <PickTable title={matchRentals.length ? `Trailer rentals for ${carrier.carrier_name}` : "Trailer rentals (no exact carrier match, showing all)"} rows={pickRentals} testid="inv-rental"
                  isPicked={(r) => hasRef(`rental:${r.id}`)} onToggle={(r) => toggleRef(`rental:${r.id}`, () => rentalItem(r))} empty="No trailer rentals on record."
                  columns={[["Trailer #", (r) => <span className="font-mono font-semibold">{r.trailer_number || "—"}</span>], ["Carrier", (r) => r.carrier_name], ["Next Due", (r) => r.next_due || "—"], ["Rent", (r) => money(r.rent_amount), true]]} />
              )}
              <div>
                {hiddenCount > 0 && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-2" data-testid="inv-undelivered-note">{hiddenCount} load{hiddenCount > 1 ? "s" : ""} not yet In Transit {hiddenCount > 1 ? "are" : "is"} hidden. Only In Transit or Delivered loads can be invoiced.</p>}
                <PickTable title={product === "Dispatch" ? "In Transit & Delivered loads: select to bill (dispatch charge auto-calculated)" : "Loads this invoice covers (optional; enter the amount per line)"} rows={pickLoads} testid="inv-load"
                  isPicked={(l) => hasRef(`load:${l.id}`)} onToggle={(l) => toggleRef(`load:${l.id}`, () => loadItem(l, product, carrier.dispatch_charges_rate))} empty="No In Transit or Delivered loads for this carrier yet."
                  columns={[["Load #", (l) => <span className="font-mono">{l.po_number || l.reference || "—"}</span>], ["Date", (l) => l.pickup_date || "—"], ["Lane", (l) => `${l.origin || "?"} → ${l.destination || "?"}`], ["Driver", (l) => l.driver_name || "—"], ["Load $", (l) => money(l.rate), true], ["Dispatch $", (l) => money(dispatchCharge(l, carrier.dispatch_charges_rate)), true]]} />
              </div>
              <LineItems items={items} onChange={setItems} />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={create} disabled={saving || (!isFactoring && lineTotal <= 0)} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="inv-submit-btn">{saving ? "Creating…" : isFactoring ? "Create Invoice" : `Create Invoice · ${money(lineTotal)}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT INVOICE */}
      <Dialog open={!!editInv} onOpenChange={(v) => !v && setEditInv(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Invoice {editInv?.number}</DialogTitle><DialogDescription className="sr-only">Edit invoice</DialogDescription></DialogHeader>
          {ef && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Customer"><Input value={ef.customer} onChange={(e) => setEf({ ...ef, customer: e.target.value })} className={inp} data-testid="edit-inv-customer" /></Field>
                <Field label="Product">
                  <Select value={ef.product} onValueChange={(v) => setEf({ ...ef, product: v })}>
                    <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                    <SelectContent>{INVOICE_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Issue Date"><DatePicker value={ef.issue_date} onChange={(v) => setEf({ ...ef, issue_date: v })} className={inp} testId="inv-edit-issue" /></Field>
                <Field label="Due Date"><DatePicker value={ef.due_date} onChange={(v) => setEf({ ...ef, due_date: v })} className={inp} testId="inv-edit-due" /></Field>
                <Field label="Payment Method">
                  <Select value={ef.payment_method || "none"} onValueChange={(v) => setEf({ ...ef, payment_method: v === "none" ? "" : v })}>
                    <SelectTrigger className={inp} data-testid="edit-inv-payment"><SelectValue placeholder="Select method" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={ef.status} onValueChange={(v) => setEf({ ...ef, status: v })}>
                    <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                    <SelectContent>{["unpaid", "sent", "paid", "overdue"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Line Items</p>
                  <Button size="sm" variant="outline" onClick={efAddItem} className="h-7 border-slate-300 bg-white text-blue-600" data-testid="edit-inv-add-item"><Plus size={14} className="mr-1" />Add</Button>
                </div>
                <div className="space-y-2">
                  {ef.items.map((it, idx) => (
                    <div key={idx} className="flex gap-2 items-center" data-testid={`edit-inv-item-${idx}`}>
                      <Input value={it.description} onChange={(e) => efItemChange(idx, "description", e.target.value)} className={`${inp} flex-1`} placeholder="Description" />
                      <Input type="number" value={it.quantity} onChange={(e) => efItemChange(idx, "quantity", e.target.value)} className={`${inp} w-16 font-mono`} placeholder="Qty" />
                      <Input type="number" value={it.rate} onChange={(e) => efItemChange(idx, "rate", e.target.value)} className={`${inp} w-24 font-mono`} placeholder="Rate" />
                      <button onClick={() => efRemoveItem(idx)} className="p-1.5 text-slate-400 hover:text-red-500 shrink-0"><X size={15} /></button>
                    </div>
                  ))}
                  {ef.items.length === 0 && <p className="text-sm text-slate-400">No line items. Add one above.</p>}
                </div>
                <div className="flex justify-end items-center gap-3 mt-3">
                  <Field label=""><div className="flex items-center gap-2"><span className="text-xs text-slate-500">Tax %</span><Input type="number" value={ef.tax_rate} onChange={(e) => setEf({ ...ef, tax_rate: e.target.value })} className={`${inp} w-20 font-mono`} /></div></Field>
                  <span className="text-slate-500 text-sm">Subtotal</span>
                  <span className="font-mono font-extrabold text-lg text-slate-900" data-testid="edit-inv-total">{money(efTotal())}</span>
                </div>
              </div>

              <Field label="Notes"><Textarea value={ef.notes} onChange={(e) => setEf({ ...ef, notes: e.target.value })} className={inp} rows={2} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditInv(null)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={saveEditInv} disabled={savingEdit} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="edit-inv-save">{savingEdit ? "Saving…" : "Save Invoice"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF PREVIEW */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="bg-white border-slate-200 max-w-4xl w-[95vw] p-0 overflow-hidden" data-testid="invoice-pdf-preview">
          <DialogHeader className="px-5 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0">
            <DialogTitle className="font-heading text-base">Invoice {preview?.number}</DialogTitle>
            <DialogDescription className="sr-only">Invoice PDF preview</DialogDescription>
          </DialogHeader>
          {preview && <iframe title="Invoice PDF" src={preview.url} className="w-full h-[78vh] border-0 block bg-slate-100" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
